import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, FormRadioGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { mapRefDataPickList } from 'shared/utils/Helpers';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';

export const MastClimbingWorkPlatformDesignDetails = React.forwardRef((props, ref) => {
  const {
    mastType: savedMastType = '',
    baseType: savedDesignUse = '',
    otherBaseType: savedOtherBaseType = '',
    maxRatedCapacity: savedMaxRatedCapacity = '',
    maxWorkingHeight: savedMaxWorkingHeight = '',
    maxFreeStandingHeight: savedMaxFreeStandingHeight = '',
    maxWindVelocityInService: savedMaxWindVelocityInService = '',
    maxVerticalTravelSpeed: savedMaxVerticalTravelSpeed = '',
  } = props.value || {};

  const [mastType, setMastType] = useState(savedMastType);
  const [baseType, setBaseType] = useState(savedDesignUse);
  const [otherBaseType, setOtherBaseType] = useState(savedOtherBaseType);
  const [maxRatedCapacity, setMaxRatedCapacity] = useState(savedMaxRatedCapacity);
  const [maxWorkingHeight, setMaxWorkingHeight] = useState(savedMaxWorkingHeight);
  const [maxFreeStandingHeight, setMaxFreeStandingHeight] = useState(savedMaxFreeStandingHeight);
  const [maxWindVelocityInService, setMaxWindVelocityInService] = useState(savedMaxWindVelocityInService);
  const [maxVerticalTravelSpeed, setMaxVerticalTravelSpeed] = useState(savedMaxVerticalTravelSpeed);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.mastClimbingWorkPlatform;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.mastClimbingWorkPlatform;

  const mastTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.mastTypeOptions]);
  const baseTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.baseTypeOptions]);

  const validators = [
    {
      fieldName: 'mastType',
      fieldValue: mastType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'baseType',
      fieldValue: baseType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'baseType_other',
      fieldValue: otherBaseType,
      isValid: (v) =>
        !isEmpty(v) || baseType !== PlantDesignRefData.plantRefs.mastClimbingWorkPlatform.subTypeRef.otherSubTypeValue,
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'maxRatedCapacity',
      fieldValue: maxRatedCapacity,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxWorkingHeight',
      fieldValue: maxWorkingHeight,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxFreeStandingHeight',
      fieldValue: maxFreeStandingHeight,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxWindVelocityInService',
      fieldValue: maxWindVelocityInService,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxVerticalTravelSpeed',
      fieldValue: maxVerticalTravelSpeed,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isValid);
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      mastType,
      baseType,
      otherBaseType,
      maxRatedCapacity,
      maxWorkingHeight,
      maxFreeStandingHeight,
      maxWindVelocityInService,
      maxVerticalTravelSpeed,
    };

    if (baseType !== PlantDesignRefData.plantRefs.mastClimbingWorkPlatform.subTypeRef.otherSubTypeValue)
      delete details.otherBaseType;

    props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    mastType,
    baseType,
    otherBaseType,
    maxRatedCapacity,
    maxWorkingHeight,
    maxFreeStandingHeight,
    maxWindVelocityInService,
    maxVerticalTravelSpeed,
  ]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <FormRadioGroup
        id="mastType"
        name="mastType"
        legend={PAGE_CONSTANTS.mastType.label}
        vertical={true}
        options={mastTypeOptions}
        onChange={setMastType}
        value={mastType}
        errorMessage={errors?.mastType?.errorMessage}
        hasError={showErrors && errors?.mastType?.invalid}
        margin={{ top: 'xl' }}
      />

      {mastType && (
        <>
          <RadioOrDropdownSelector
            id="baseType"
            name="baseType"
            options={baseTypeOptions}
            title={PAGE_CONSTANTS.baseType.label}
            errorMessage={errors?.baseType?.errorMessage}
            hasError={showErrors && errors?.baseType?.invalid}
            onChange={(v) => {
              setBaseType(v);
              setOtherBaseType('');
            }}
            value={baseType}
            showExtraInput={
              baseType === PlantDesignRefData.plantRefs.mastClimbingWorkPlatform.subTypeRef.otherSubTypeValue
            }
            onChangeExtraInput={setOtherBaseType}
            extraInputValue={otherBaseType}
            extraInputErrorMessage={errors?.baseType_other?.errorMessage}
            extraInputHasError={showErrors && errors?.baseType_other?.invalid}
            extraInputTitle={'Please describe the type of the base'}
            margin={{ top: 'xl' }}
            extraInputType="textarea"
          />
          <FormGroup
            id="maxRatedCapacity"
            label={PAGE_CONSTANTS.maxRatedCapacity.label}
            isRequired
            errorMessage={errors?.maxRatedCapacity?.errorMessage}
            hasError={showErrors && errors?.maxRatedCapacity?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'xl' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.maxRatedCapacity.suffix}
              onChange={(e) => setMaxRatedCapacity(e.target.value)}
              value={maxRatedCapacity}
              inputWidth="lg"
            />
          </FormGroup>
          <FormGroup
            id="maxWorkingHeight"
            label={PAGE_CONSTANTS.maxWorkingHeight.label}
            isRequired
            errorMessage={errors?.maxWorkingHeight?.errorMessage}
            hasError={showErrors && errors?.maxWorkingHeight?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.maxWorkingHeight.suffix}
              onChange={(e) => setMaxWorkingHeight(e.target.value)}
              value={maxWorkingHeight}
              inputWidth="md"
            />
          </FormGroup>
          <FormGroup
            id="maxFreeStandingHeight"
            label={PAGE_CONSTANTS.maxFreeStandingHeight.label}
            isRequired
            errorMessage={errors?.maxFreeStandingHeight?.errorMessage}
            hasError={showErrors && errors?.maxFreeStandingHeight?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.maxFreeStandingHeight.suffix}
              onChange={(e) => setMaxFreeStandingHeight(e.target.value)}
              value={maxFreeStandingHeight}
              inputWidth="md"
            />
          </FormGroup>
          <FormGroup
            id="maxWindVelocityInService"
            label={PAGE_CONSTANTS.maxWindVelocityInService.label}
            isRequired
            errorMessage={errors?.maxWindVelocityInService?.errorMessage}
            hasError={showErrors && errors?.maxWindVelocityInService?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.maxWindVelocityInService.suffix}
              onChange={(e) => setMaxWindVelocityInService(e.target.value)}
              value={maxWindVelocityInService}
              inputWidth="md"
            />
          </FormGroup>
          <FormGroup
            id="maxVerticalTravelSpeed"
            label={PAGE_CONSTANTS.maxVerticalTravelSpeed.label}
            isRequired
            errorMessage={errors?.maxVerticalTravelSpeed?.errorMessage}
            hasError={showErrors && errors?.maxVerticalTravelSpeed?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.maxVerticalTravelSpeed.suffix}
              onChange={(e) => setMaxVerticalTravelSpeed(e.target.value)}
              value={maxVerticalTravelSpeed}
              inputWidth="md"
            />
          </FormGroup>
        </>
      )}
    </>
  );
});

MastClimbingWorkPlatformDesignDetails.displayName = 'MastClimbingWorkPlatformDesignDetails';

MastClimbingWorkPlatformDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
